<template>
  <div>
    <!-- Accordion for showing document support information. For details on the implementation see this oruga doc https://oruga.io/components/Collapse.html#accordion -->
    <o-collapse
      class="tw-text-white tw-max-w-xs"
      animation="slide"
      :open="isFAQOpen"
      @open="isFAQOpen = true"
      @close="isFAQOpen = false"
    >
      <template #trigger>
        <div class="tw-flex tw-items-center tw-cursor-pointer tw-px-4 tw-py-2 tw-border tw-rounded-lg tw-shadow tw-mb-4 tw-text-white">
          <o-icon
            :icon="isFAQOpen ? 'caret-right' : 'caret-down'"
            class="tw-mr-2"
          />
          <h2 class="tw-text tw-font-bold">
            More FAQ
          </h2>
        </div>
      </template>

      <section v-show="isFAQOpen">
        <!-- This will show/hide the content when the FAQ title is clicked -->
        <div
          v-for="(collapse, index) of supportAccordion"
          :key="'supportAccordion'+index"
          class=""
        >
          <o-collapse
            animation="slide"
            :open="isOpen == 'supportAccordion'+index"
            @open="isOpen = 'supportAccordion'+index"
          >
            <template #trigger="props">
              <div
                class="card-header"
                role="button"
              >
                <p class="card-header-title tw-text-white">
                  {{ collapse.title }}
                </p>
                <a class="card-header-icon">
                  <o-icon :icon="props.open ? 'caret-up' : 'caret-down'" />
                </a>
              </div>
            </template>
            <div class="card-content">
              <div
                class="content"
                v-html="collapse.content"
              />
            </div>
          </o-collapse>
        </div>
      </section>
    </o-collapse>
  </div>
</template>


<script>
import { mapStores } from 'pinia';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
// import SubComponent from '../components/SubComponent'

export default {
  name: 'AdvisorCoreFAQ',
  components: { /* Subcomponents */ },
  props: [/* Inputs */],
  data() {
    return {
      isFAQOpen: false,
      isOpen: null,
    };
  },
  computed: { /*
    Creates a new property
    Updates when any dependant property changes */
    ...mapStores(useAdvisorStore),
    supportAccordion() {
      const getStandardSupportAccordion = [
      {
          title: 'What is a "Standard Document", and what is the upload success rate?',
          content: `
            Standard documents have a 96% first-try upload success rate. Support: 1 Business Day turnaround for any failed upload fitting the criteria below:
            <ul>
              <li>Statements are unaltered, complete brokerage statements containing holdings in sequential order.</li>
              <li>Statements include ticker symbols and/or CUSIPs</li>
              <li>Must be only one statement (not multiple statements combined into one document).</li>
              <li>If the upload is a scan or picture, it must be legible, with no information cut off. All pages/data must be in frame.</li>
            </ul>
            What documents are not classified as "Standard Documents"?:
            <ul>
              <li>Missing pages or pages out of order.</li>
              <li>Multiple pages combined in one file.</li>
              <li>Page prints or screenshots of a web portal view.</li>
              <li>Any non-brokerage statement document such as: performance review, transaction report, account opening statement, etc.</li>
              <li>Statements that don't contain ticker symbols or CUSIPs.</li>
            </ul>
          `,
        },
        {
          title: 'What is an "Advanced Document", and what is the upload success rate?',
          content: `
            Advanced documents have a 50% first-try upload success rate. Support: 2 Business Day turnaround for any failed upload. Documents covered include:
            <ul>
              <li>Poor quality scans or images.</li>
              <li>Statements with pages missing or out-of-order.</li>
              <li>Statements that don't contain ticker symbols or CUSIPs.</li>
              <li>Non-brokerage statements that contain holdings (e.g. screenshots of a web portal, performance reports, etc.).</li>
              <li>Multiple statements combined into one file.</li>
            </ul>
          `,
        },
        {
          title: 'What if my document doesn\'t contain tickers or CUSIPs?',
          content: `
            <ol>
              <li>YourStake will process the holdings and return you the names of the holdings in spreadsheet form.</li>
              <li>You will receive a notification directing you to use our "magic ticker fill", which matches the holdings to the closest-match publicly traded ticker.</li>
            </ol>
          `,
        },
        {
          title: 'What validation checks does YourStake do on the uploads?',
          content: `
            If any of the below validation checks fail, YourStake's team will communicate with you and resolve the issue for you within the support window specified above.
            <ul>
              <li>YourStake ensures the sum of all the holdings of every account matches the account summary listed in the statement.</li>
              <li>YourStake checks to make sure everything that we classify as a holding has a quantity/number of shares listed.</li>
              <li>YourStake checks every extracted ticker with our database of tickers to make sure the symbol was extracted correctly, and labels anything that didn't match as unmatched. You can edit these tickers or assign a proxy ticker in the Holdings tab of this page.</li>
            </ul>
          `,
        },
        {
          title: 'What should I do if I receive a warning message?',
          content: `
            <ul>
              <li>Anytime you receive a warning message, YourStake's team will be notified, look into the issue, communicate the status to you via email, and begin resolving the issue (if one exists).</li>
              <li>You will still receive a spreadsheet of the holdings that were extracted successfully, and can run analytics on those holdings.</li>
              <li>Some warning messages will just ask you to double check that everything worked. For example, if you upload a document that doesn't contain an account total, our system will raise a warning that we were not able to validate the account total, and suggest that you double check.</li>
              <li>If you would like to correct a warning on your own, you may do so by editing the holdings on the "Holdings" tab of this page.</li>
            </ul>
          `,
        },
        {
          title: 'How do I combine multiple statements into one portfolio?',
          content: `
            <ul>
              <li>You can combine any combination of statements or portfolios into a single aggregate portfolio.</li>
              <li>Simply upload or search + select multiple items in the section titled “1. Your Portfolio(s)”. Then click “Run analytics”, and it will automatically create a combined portfolio out of all the ones you selected.</li>
            </ul>
          `,
        },
      ];
      const getCreditBasedSupportAccordion= [
        {
          title: 'What is a "Standard Document", and what is the upload success rate?',
          content: `
            Standard documents have a 96% first-try upload success rate. Support: 1 Business Day turnaround for any failed upload fitting the criteria below:
            <ul>
              <li>Statements are unaltered, complete brokerage statements containing holdings in sequential order.</li>
              <li>Statements include ticker symbols and/or CUSIPs</li>
              <li>Must be only one statement (not multiple statements combined into one document).</li>
              <li>If the upload is a scan or picture, it must be legible, with no information cut off. All pages/data must be in frame.</li>
            </ul>
            What documents are not classified as "Standard Documents"?:
            <ul>
              <li>Missing pages or pages out of order.</li>
              <li>Multiple pages combined in one file.</li>
              <li>Page prints or screenshots of a web portal view.</li>
              <li>Any non-brokerage statement document such as: performance review, transaction report, account opening statement, etc.</li>
              <li>Statements that don't contain ticker symbols or CUSIPs.</li>
            </ul>
          `,
        },
        {
          title: 'What is an "Advanced Document", and what is the upload success rate?',
          content: `
            Advanced documents have a 50% first-try upload success rate. Support: 2 Business Day turnaround for any failed upload. Documents covered include:
            <ul>
              <li>Poor quality scans or images.</li>
              <li>Statements with pages missing or out-of-order.</li>
              <li>Statements that don't contain ticker symbols or CUSIPs.</li>
              <li>Non-brokerage statements that contain holdings (e.g. screenshots of a web portal, performance reports, etc.).</li>
              <li>Multiple statements combined into one file.</li>
            </ul>
          `,
        },
        {
          title: 'What are the costs for standard and advanced documents?',
          content: `
            Standard documents are $3 per upload and advanced documents are $9 per upload.
          `,
        },
        {
          title: 'How are credits deducted from my account?',
          content: `
            Our system automatically deducts the appropriate number of credits from my account once an upload is processed. This is adjusted on a weekly basis if necessary (see below for more details on classification).
          `,
        },
        {
          title: 'How do I provision more credits?',
          content: `
            To provision a batch of 1,000 credits please send an email to support@yourstake.org.
          `,
        },
        {
          title: 'How are documents classified as standard or advanced?',
          content: `
            Our system automatically classifies documents as standard or advanced based on characteristics of the uploaded document, and then our team reviews any advanced documents and reclassifies them as standard if necessary. 
            This is done on a weekly basis, and you will receive an email with a summary of the documents uploaded and their classifications.
          `,
        },
        {
          title: 'What if my document doesn\'t contain tickers or CUSIPs?',
          content: `
            <ol>
              <li>YourStake will process the holdings and return you the names of the holdings in spreadsheet form.</li>
              <li>You will receive a notification directing you to use our "magic ticker fill", which matches the holdings to the closest-match publicly traded ticker.</li>
            </ol>
          `,
        },
        {
          title: 'What validation checks does YourStake do on the uploads?',
          content: `
            If any of the below validation checks fail, YourStake's team will communicate with you and resolve the issue for you within the support window specified above.
            <ul>
              <li>YourStake ensures the sum of all the holdings of every account matches the account summary listed in the statement.</li>
              <li>YourStake checks to make sure everything that we classify as a holding has a quantity/number of shares listed.</li>
              <li>YourStake checks every extracted ticker with our database of tickers to make sure the symbol was extracted correctly, and labels anything that didn't match as unmatched. You can edit these tickers or assign a proxy ticker in the Holdings tab of this page.</li>
            </ul>
          `,
        },
        {
          title: 'What should I do if I receive a warning message?',
          content: `
            <ul>
              <li>Anytime you receive a warning message, YourStake's team will be notified, look into the issue, communicate the status to you via email, and begin resolving the issue (if one exists).</li>
              <li>You will still receive a spreadsheet of the holdings that were extracted successfully, and can run analytics on those holdings.</li>
              <li>Some warning messages will just ask you to double check that everything worked. For example, if you upload a document that doesn't contain an account total, our system will raise a warning that we were not able to validate the account total, and suggest that you double check.</li>
              <li>If you would like to correct a warning on your own, you may do so by editing the holdings on the "Holdings" tab of this page.</li>
            </ul>
          `,
        },
        {
          title: 'How do I combine multiple statements into one portfolio?',
          content: `
            <ul>
              <li>You can combine any combination of statements or portfolios into a single aggregate portfolio.</li>
              <li>Simply upload or search + select multiple items in the section titled “1. Your Portfolio(s)”. Then click “Run analytics”, and it will automatically create a combined portfolio out of all the ones you selected.</li>
            </ul>
          `,
        },
      ];

      return this.advisorStore.advisor?.firm?.credit_based_firm ? getCreditBasedSupportAccordion : getStandardSupportAccordion;
    },
  },
  watch: { /*
    Watches an existing property
    Only runs when the watched property changes */
  },
  beforeCreate() {},
  created() {},
  mounted() {},
  updated() {},
  methods: {},
};
</script>


  <style scoped>

  </style>
